import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import type { ApplicationRound } from "../../../../core/data/redux/recruitment/types";
import {
  selectApplicationRounds,
  selectApplicationRoundsLoading,
} from "../../../../core/data/redux/recruitment/selectors";
import { fetchApplicationRounds } from "../../../../core/data/redux/recruitment/thunks";
import RoundCard from "./RoundCard";

interface Props {
  applicationId: number;
}

const ApplicationRoundPipeline: React.FC<Props> = ({ applicationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const rounds = useSelector(selectApplicationRounds);
  const loading = useSelector(selectApplicationRoundsLoading);

  useEffect(() => {
    if (applicationId) {
      dispatch(fetchApplicationRounds(applicationId));
    }
  }, [dispatch, applicationId]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div className="spinner-border spinner-border-sm text-primary me-2" />
        <span style={{ color: "#94a3b8", fontSize: 13 }}>Loading pipeline…</span>
      </div>
    );
  }

  if (!rounds || rounds.length === 0) {
    return (
      <div
        style={{
          padding: "32px 24px",
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 13,
          background: "#f8fafc",
          borderRadius: 12,
          border: "1px dashed #e2e8f0",
        }}
      >
        <i className="ti ti-route-off" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
        No pipeline configured for this application's job.<br />
        <span style={{ fontSize: 11, opacity: 0.7 }}>
          Configure interview rounds on the job to enable the pipeline view.
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* Stepper connector line */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          marginBottom: 20,
          overflowX: "auto",
          paddingBottom: 4,
        }}
      >
        {rounds.map((round: ApplicationRound, idx: number) => {
          const isActive = !round.is_terminal && round.round_status !== "pending";
          const isPassed = round.round_status === "passed";
          const isFailed = round.round_status === "failed";
          const isPending = round.round_status === "pending";

          const dotColor = isPassed
            ? "#00C851"
            : isFailed
              ? "#E74C3C"
              : isActive
                ? "#9B59B6"
                : "#cbd5e1";

          return (
            <React.Fragment key={round.id}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 90 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: dotColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    boxShadow: isActive ? `0 0 0 4px ${dotColor}30` : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isPassed ? (
                    <i className="ti ti-check" />
                  ) : isFailed ? (
                    <i className="ti ti-x" />
                  ) : (
                    round.stage_order
                  )}
                </div>
                <span
                  style={{
                    fontSize: 10,
                    marginTop: 4,
                    color: isPending ? "#94a3b8" : "#475569",
                    fontWeight: isActive ? 600 : 400,
                    whiteSpace: "nowrap",
                    maxWidth: 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={round.round_name}
                >
                  {round.round_name}
                </span>
              </div>
              {idx < rounds.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    minWidth: 24,
                    height: 2,
                    background: isPassed ? "#00C851" : "#e2e8f0",
                    marginTop: -14,
                    transition: "background 0.3s ease",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Detailed round cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rounds.map((round: ApplicationRound) => (
          <RoundCard
            key={round.id}
            round={round}
            applicationId={applicationId}
          />
        ))}
      </div>
    </div>
  );
};

export default ApplicationRoundPipeline;
